import { LockClosedIcon } from "@radix-ui/react-icons";

import { Button, Tooltip } from "@radix-ui/themes";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { twMerge } from "tailwind-merge";
import { STUDIO_ACTIONS } from "./studio-actions-config";

interface StudioActionGridProps {
  onAction: (action: string) => void;
  selectedAction?: string;
  lockedActions?: string[];
  onLockedClick?: () => void;
  hiddenActions?: string[];
}

export const StudioActionGrid = ({
  onAction,
  selectedAction = "information",
  lockedActions = [],
  onLockedClick,
  hiddenActions,
}: StudioActionGridProps) => {
  const { t } = useTranslation();

  const actions = STUDIO_ACTIONS.map((action) => ({
    ...action,
    labelKey: t(action.labelKey),
    tooltip: t(action.tooltipKey),
  })).filter((action) => !hiddenActions || !hiddenActions.includes(action.id));
  return (
    <div className="overflow-auto flex -mx-4 px-4 md:mx-0 md:px-0 md:grid md:grid-cols-4 gap-1">
      {actions.map((action, index) => {
        const IconComponent = action.icon;
        const isSelected = action.id === selectedAction;
        const isLocked = lockedActions.includes(action.id);
        return (
          <motion.div
            key={action.id}
            initial={{ opacity: 0, y: 0 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{
              duration: 0.2,
              delay: index * 0.03,
              ease: "easeOut",
            }}
            className={twMerge(index === 0 && "col-span-2")}
          >
            <Tooltip content={action.tooltip}>
              <Button
                size="2"
                variant={isSelected && !isLocked ? "solid" : "soft"}
                color={isSelected && !isLocked ? "blue" : "gray"}
                className={`p-2 flex flex-col items-center justify-center gap-1 text-center rounded-lg overflow-hidden w-20 md:w-full h-14 relative ${
                  isLocked ? "opacity-50" : ""
                }`}
                onClick={() =>
                  isLocked ? onLockedClick?.() : onAction(action.id)
                }
              >
                <IconComponent className="h-4 w-4" />
                <span className="text-xs leading-tight whitespace-nowrap text-ellipsis">
                  {action.labelKey}
                </span>
                {isLocked && (
                  <LockClosedIcon className="h-3 w-3 absolute top-1 right-1 text-slate-400" />
                )}
              </Button>
            </Tooltip>
          </motion.div>
        );
      })}
    </div>
  );
};
